import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FixedSizeList as List, ListOnItemsRenderedProps } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import API from '../../../utils/api'
import { useToken } from '../../hooks'
import { extractDateTime } from '../../../utils/extractDateTime'
import styled from 'styled-components'
import dayjs from 'dayjs'
import DateFilter from '../DateFilter'
import { Button, Select } from 'antd'
import { useExchangeStore } from '../../hooks/trade/exchangeStore'
import BigNumber from 'bignumber.js'
import { ThemeContext } from '../../../theme/ThemeProvider'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useTimezone } from '../../hooks/timezone'
import { DeskTopCalVW } from '../../../utils/CalVW'
import DpPrice from '../../components/DpPrice'

export const OrderListTable = ({ mobile = false, userid = null }) => {
	const { data: token } = useToken()
	const [tableData, setTableData] = useState<any>([])
	const [page, setPage] = useState(1)
	const [hasMore, setHasMore] = useState(true)
	const [loading, setLoading] = useState(false)
	const [sortKey, setSortKey] = useState(null)
	const [sortAsc, setSortAsc] = useState(true)
	const [totalCount, setTotalCount] = useState(0)

	let now = dayjs().format('YYYY-MM-DD')
	const today = dayjs().format('YYYY-MM-DD')
	const prev = dayjs(today).subtract(1, 'day').format('YYYY-MM-DD')

	//새벽 12시~오전 7시 사이에 조회할 경우 전날 데이터 조회
	if (dayjs().hour() < 7) {
		now = prev
	} else {
		now = today
	}


	const [startDate, setStartDate] = useState<any>(now)
	const [endDate, setEndDate] = useState<any>(now)
	const [orderType, setOrderType] = useState<any>('')
	const [ep, setEp] = useState<any>('*')
	const [pageData, setPageData] = useState<any>(100)

	const { EPs } = useExchangeStore()

	const apikey = useMemo(() => {
		return `/${userid ? 'group' : 'trade'}/orderlist?type=VER1${
			startDate ? `&sdate=${startDate}` : ''
		}${
			endDate ? `&edate=${endDate}` : ''
		}&ordertype=${orderType}&ep_id=${ep}${
			userid ? '&user_id=' + userid : ''
		}&t=`
	}, [startDate, endDate, orderType, ep, userid])

	useEffect(() => {
		setPage(1)
		setTableData([])
		setHasMore(true)
		fetchData(1)
	}, [startDate, endDate, orderType, ep, pageData])

	useEffect(() => {
		fetchData(page)
	}, [page, apikey])

	const resetAndFetch = () => {
		setSortKey(null)
		setSortAsc(true)
		setPage(1)
		setHasMore(true)
		setTableData([])
		fetchData(1)
	}

	useEffect(() => {
		resetAndFetch()
	}, [userid])

	const fetchData = async (pageNum: number) => {
		if (pageNum !== 1 && (loading || !hasMore)) return

		setLoading(true)
		const response = await API.get(
			`${apikey}&maxData=${pageData}&page=${pageNum}`,
			token + '',
			{},
			false
		)
		const newList = response?.result?.result?.list || []
		setTotalCount(response?.result?.result?.totalCount || 0)

		setTableData(prev =>
			pageNum === 1 ? [...newList] : [...prev, ...newList]
		)
		if (newList?.length < 100) setHasMore(false)
		setLoading(false)
	}

	const handleItemsRendered = ({ visibleStopIndex }) => {
		if (!loading && hasMore && visibleStopIndex >= tableData?.length - 10) {
			setPage(prev => prev + 1)
		}
	}

	const sortedData = useMemo(() => {
		if (!sortKey) return tableData
		return [...tableData]?.sort((a, b) => {
			if (sortAsc) {
				return a[sortKey] > b[sortKey] ? 1 : -1
			} else {
				return a[sortKey] < b[sortKey] ? 1 : -1
			}
		})
	}, [tableData, sortKey, sortAsc])

	const toggleSort = key => {
		if (sortKey === key) {
			if (sortAsc === true) {
				setSortAsc(false)
			} else {
				setSortKey(null)
				setSortAsc(true)
			}
		} else {
			setSortKey(key)
			setSortAsc(true)
		}
	}

	const columns = [
		{ label: '주문', key: 'id', width: '5.2%' },
		{ label: '원주문', key: 'original_id', width: '5.2%' },
		{ label: '종목코드', key: 'pairs', width: '5.2%' },
		{ label: '종목명', key: 'groupname', width: '7.2%' },
		{ label: '거래소', key: 'exchange_code', width: '4.2%' },
		{ label: '상태', key: 'open_close', width: '2.7%' },
		{ label: '포지션', key: 'long_short', width: '2.7%' },
		{ label: '구분', key: 'reduce_only', width: '4.2%' },
		{ label: '주문수', key: 'amount', width: '2.7%' },
		{ label: '주문단가', key: 'order_price', width: '5.2%' },
		{ label: '체결수', key: 'remain_amount', width: '2.7%' },
		{ label: '체결단가', key: 'trade_price', width: '5.2%' },
		{ label: '수수료', key: 'fee_price', width: '5.2%' },
		{ label: '매매손익', key: 'pnl_price', width: '5.2%' },
		{ label: '실잔액', key: 'balance_futures', width: '5.2%' },
		{ label: '주문일자', key: 'created_at', width: '10.2%' },
		{ label: '체결일시', key: 'traded_at', width: '10.2%' },
		{ label: '플랫폼', key: 'platform', width: '5.2%' },
		{ label: '비고', key: 'status', width: '5.2%' },
	]

	const Row = ({ index, style }) => {
		const record = sortedData[index]
		const { canvasColor } = useContext(ThemeContext)

		const statusList: any = []

		if (record?.f == 1) {
			statusList.push({ color: '#F05551', text: '로스컷' })
		} else if (record?.f == 3) {
			statusList.push({ color: '#F05551', text: '장마감' })
		} else if (record?.f == 5) {
			statusList.push({ color: '#311b1b', text: 'MIT' })
		} else if (record?.f == 4) {
			statusList.push({ color: '#F05551', text: '오버부족' })
		} else {
			statusList.push({
				color:
					record?.status == 2
						? '#000000'
						: record?.status == 3
						? '#000000'
						: 'default',
				text:
					record?.status == 1
						? '미체결'
						: record?.status == 2
						? '체결'
						: record?.status == 3
						? '취소'
						: '-',
			})
		}

		if (record?.reduce_position == 5) {
			statusList.push({ color: '#272727', text: 'MIT' })
		} else if (record?.reduce_position == 2) {
			statusList.push({ color: '#F05551', text: '익절' })
		} else if (record?.reduce_position == 6) {
			statusList.push({ color: '#007EDA', text: '손절' })
		} else if (record?.reduce_position == 4) {
			statusList.push({ color: '#F05551', text: '전체청산' })
		} else if (record?.reduce_position == 3) {
			statusList.push({ color: '#F05551', text: '현종목청산' })
		}

		return (
			<div
				style={{
					...style,
					display: 'flex',
					borderBottom: '1px solid #ccc',
				}}>
				<CellItem style={{ width: columns[0].width }}>
					{BigNumber(record?.id?.toString()).toFormat(0)}
				</CellItem>
				<CellItem style={{ width: columns[1].width }}>
					{record?.original_id}
				</CellItem>
				<CellItem style={{ width: columns[2].width }}>
					{record?.pairs}
				</CellItem>
				<CellItem style={{ width: columns[3].width }}>
					{record?.groupname}
				</CellItem>
				<CellItem style={{ width: columns[4].width }}>
					{record?.exchange_code}
				</CellItem>
				<CellItem
					style={{
						width: columns[5].width,
						color: record?.open_close === 'C' ? 'red' : 'black',
					}}>
					{record?.open_close === 'C' ? '청산' : '진입'}
				</CellItem>
				<CellItem
					style={{
						width: columns[6].width,
						color: record?.long_short === 'L' ? 'red' : 'blue',
					}}>
					{record?.long_short === 'L' ? '매수' : '매도'}
				</CellItem>
				<CellItem style={{ width: columns[7].width }}>
					{record?.reduce_only == 1
						? 'MIT'
						: record?.limit_market == 'M'
						? '시장가'
						: '지정가'}
				</CellItem>
				<CellItem style={{ width: columns[8].width }}>
					{record?.amount}
				</CellItem>
				<CellItem
					style={{
						width: columns[9].width,
						fontWeight: 'bold',
					}}>
					<DpPrice price={record?.order_price} groupname={record?.groupname} />
					{/*{BigNumber(record?.order_price?.toString())?.toFormat(2)}*/}
				</CellItem>
				<CellItem style={{ width: columns[10].width }}>
					{new BigNumber(record?.amount)
						.minus(record?.remain_amount)
						.multipliedBy(record?.leverage)
						.toFormat(0)}
				</CellItem>
				<CellItem
					style={{
						width: columns[11].width,
						fontWeight: 'bold',
					}}>
					<DpPrice price={record?.trade_price} groupname={record?.groupname} />
					{/*{record?.trade_price ? BigNumber(record?.trade_price?.toString())?.toFormat(2) : '-'}*/}
				</CellItem>
				<CellItem
					style={{
						fontWeight: 'bold',
						width: columns[12].width,
						color:
							-Math.abs(record?.fee_price?.toString()) > 0
								? canvasColor.plus
								: -Math.abs(record?.fee_price?.toString()) < 0
								? canvasColor.minus
								: canvasColor.text,
					}}>
					{BigNumber(-Math.abs(record?.fee_price?.toString()))?.toFormat(
						0
					)}
				</CellItem>
				<CellItem
					style={{
						fontWeight: 'bold',
						width: columns[13].width,
						color:
							record?.pnl_price?.toString() > 0
								? canvasColor.plus
								: record?.pnl_price?.toString() < 0
								? canvasColor.minus
								: canvasColor.text,
					}}>
					{BigNumber(record?.pnl_price?.toString())?.toFormat(0)}
				</CellItem>
				<CellItem
					style={{
						width: columns[14].width,
						fontWeight: 'bold',
					}}>
					{BigNumber(record?.balance_futures?.toString())?.toFormat(0)}
				</CellItem>
				<CellItem style={{ width: columns[15].width }}>
					{useTimezone(record?.created_at, 'YYYY-MM-DD HH:mm:ss', 9)}
				</CellItem>
				<CellItem style={{ width: columns[16].width }}>
					{useTimezone(record?.traded_at, 'YYYY-MM-DD HH:mm:ss', 9)}
				</CellItem>
				<CellItem
					style={{
						fontWeight: 'bold',
						width: columns[17].width,
						color:
							record?.platform === 0
								? '#5877ff'
								: record?.platform === 1
								? '#00c79c'
								: record?.platform === 2
								? '#6e15f4'
								: 'red',
						// color: record.platform === 9 ? 'red' : 'white',
					}}>
					{['WTS', 'MTS', 'HTS', '서버'][record?.platform] || '서버'}
				</CellItem>
				<LastCellItem style={{ width: columns[18].width }}>
					<div
						style={{
							fontWeight: 'bold',
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							whiteSpace: 'pre-wrap',
							color:
								statusList?.length === 1
									? statusList[0]?.color
									: undefined,
						}}>
						{statusList?.length === 1
							? statusList[0]?.text
							: statusList?.map((i, idx) => (
									<div
										key={`orderList_status_${idx}`}
										style={{ color: i?.color }}>
										{i?.text}
										{idx < statusList?.length - 1 && ' / '}
									</div>
							  ))}
					</div>
				</LastCellItem>
			</div>
		)
	}

	return (
		<div
			style={{
				height: '750px',
				border: '1px solid #ccc',
				position: 'relative',
			}}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}>
				<div>
					<DateFilter
						setStartDate={setStartDate}
						setEndDate={setEndDate}
						startDate={startDate}
						endDate={endDate}
						mobile={mobile}
					/>

					<>
						<Select
							value={ep}
							onChange={e => setEp(e)}
							style={{ minWidth: 200 }}>
							<Select.Option value="*" key={'all'}>
								전체종목
							</Select.Option>
							{EPs?.map((ep, i) => (
								<Select.Option
									value={ep?.id}
									key={'option' + ep?.id}>
									{ep?.pairs} ({ep?.groupname})
								</Select.Option>
							))}
						</Select>

						<Select
							value={orderType}
							onChange={e => setOrderType(e)}
							style={{ minWidth: 150 }}>
							<Select.Option key={'s1'} value="">
								최근순
							</Select.Option>
							<Select.Option key={'s2'} value="2">
								오래된순
							</Select.Option>
							<Select.Option key={'s6'} value="6">
								체결일자 최근순
							</Select.Option>
							<Select.Option key={'s5'} value="5">
								체결일자 오래된순
							</Select.Option>
							<Select.Option key={'s3'} value="4">
								매매수익순
							</Select.Option>
							<Select.Option key={'s4'} value="3">
								매매손해순
							</Select.Option>
						</Select>
					</>
				</div>

				<div>
					{/*<Button*/}
					{/*	// onClick={exportToExcel}*/}
					{/*	icon={<DownloadOutlined />}*/}
					{/*	type={undefined}*/}
					{/*/>*/}

					<Button
						onClick={() => resetAndFetch()}
						type={undefined}
						icon={<ReloadOutlined />}
					/>
				</div>
			</div>

			<div style={{ height: '660px', overflowX: 'auto' }}>
				<div
					style={{
						width:
							window.innerWidth > 1920
								? '76.9vw'
								: window.innerWidth < 1370
								? '1300px'
								: '100%',
						height: '100%',
					}}>
					<HeaderRow>
						{columns.map(({ label, key, width }, i) => (
							<HeaderCell
								key={i}
								width={width}
								onClick={() =>
									key !== 'status' && toggleSort(key)
								}>
								{label}
								{sortKey === key && (sortAsc ? ' ▲' : ' ▼')}
							</HeaderCell>
						))}
					</HeaderRow>

					<AutoSizer disableWidth>
						{({ height, width }) => (
							<List
								height={height - 40}
								width={
									window.innerWidth > 1920
										? '76.9vw'
										: window.innerWidth < 1370
										? '1300px'
										: '100%'
								}
								itemCount={sortedData.length}
								itemSize={30}
								onItemsRendered={handleItemsRendered}
								overscanCount={10}>
								{Row}
							</List>
						)}
					</AutoSizer>
				</div>
			</div>

			<div
				style={{
					padding: '12px',
					fontWeight: 'bold',
					background: '#fafafa',
					borderTop: '1px solid #ddd',
					position: 'relative',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
				}}>
				<div>
					Total:{' '}
					{loading
						? '로딩중...'
						: tableData.length.toLocaleString() +
						  ' / ' +
						  totalCount.toLocaleString()}
				</div>
				<Select
					value={pageData}
					onChange={e => setPageData(e)}
					style={{ minWidth: 200 }}
					options={[
						{ value: 100, label: '100 / page' },
						{ value: 500, label: '500 / page' },
						{ value: 1000, label: '1000 / page' },
					]}
				/>
			</div>
		</div>
	)
}

const BaseCell = styled.div<{ width?: string }>`
	width: ${({ width }) => width || 'auto'};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	border-right: 1px solid #ccc;
	white-space: nowrap;

	@media (min-width: 1920px) {
		font-size: calc(12px + (6 * ((100vw - 1920px) / 1000)));
	}

	@media (max-width: 1600px) {
		font-size: 10px;
	}

	@media (max-width: 1370px) {
		font-size: 12px;
	}
`


const HeaderCell = styled(BaseCell)`
	font-weight: bold;
	cursor: pointer;
	background: #f8f8f8;
`

const CellItem = styled(BaseCell)``
const LastCellItem = styled(BaseCell)``

const HeaderRow = styled.div`
	display: flex;
	font-weight: bold;
	font-size: 12px;
	border-bottom: 2px solid #888;
	background: #f8f8f8;
	height: 30px;
	width: 99.6%;
`

// const HeaderCell = styled.div<{ width: string }>`
//   width: ${({ width }) => width};
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   cursor: pointer;
// `;